import React, { useContext, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import BoardCodeForm from '../../components/Code/BoardCodeForm';
import OrgSelect from '../../components/OrgSelect/OrgSelect';
import { AuthContext } from '../../context/AuthContext';

function BoardCodeCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const M_ComCode = location.state?.M_ComCode || '';
  const M_ComName = location.state?.M_ComName || '';
  const { token } = useContext(AuthContext);

  // 권한 관리 상태
  const [boardAuths, setBoardAuths] = useState([]);
  const [showOrgSelect, setShowOrgSelect] = useState(false);
  const [selectedAuthType, setSelectedAuthType] = useState('read');

  const handleSubmit = async (formData) => {
    try {
      // 게시판 코드 생성
      const response = await axios.post('/api/board-codes', formData, {
        headers: { Authorization: `Bearer ${token}`, 'x-com-code': M_ComCode },
      });

      const newBoardCode = response.data;

      // 권한 일괄 생성 (게시판 코드가 생성된 후)
      if (Array.isArray(boardAuths) && boardAuths.length > 0) {
        const authsWithBoardCode = boardAuths.map(auth => ({
          ...auth,
          board_code: newBoardCode.code
        }));

        await axios.post('/api/board-codes/auths/bulk', authsWithBoardCode, {
          headers: { Authorization: `Bearer ${token}`, 'x-com-code': M_ComCode },
        });
      }

      alert('게시판 코드가 등록되었습니다');
      navigate('/board-codes', { state: { M_ComCode, M_ComName } });
    } catch (err) {
      console.error('게시판 코드 등록 실패:', err);
      alert(err.response?.data?.message || '등록 중 오류 발생');
    }
  };

  // 권한 관리 함수들
  const handleAddAuth = (authType) => {
    setSelectedAuthType(authType);
    setShowOrgSelect(true);
  };

  const handleOrgSelectConfirm = (selectedTargets) => {
    const newAuths = selectedTargets.map(target => ({
      target_type: target.target_type,
      target_id: target.target_id,
      target_name: target.target_name,
      auth_type: selectedAuthType
    }));

    // 중복 권한 제거
    const filteredAuths = newAuths.filter(newAuth =>
      !boardAuths.some(existing =>
        existing.target_type === newAuth.target_type &&
        existing.target_id === newAuth.target_id &&
        existing.auth_type === newAuth.auth_type
      )
    );

    setBoardAuths(prev => [...prev, ...filteredAuths]);
    setShowOrgSelect(false);
  };

  const handleOrgSelectCancel = () => {
    setShowOrgSelect(false);
  };

  const handleRemoveAuth = (index) => {
    setBoardAuths(prev => prev.filter((_, i) => i !== index));
  };

  const getAuthTypeLabel = (authType) => {
    const labels = { 'read': '읽기', 'write': '쓰기', 'manage': '관리' };
    return labels[authType] || authType;
  };

  const getTargetTypeLabel = (targetType) => {
    return targetType === 'user' ? '사용자' : '부서';
  };

  return (
    <div className="view-boardcode container-fluid">
      <div className="row">
        <div className="col-12">

          {/* 헤더 영역 (상단 고정) */}
          <div className="boardcode-header-fixed d-flex justify-content-end gap-2">
            <button
              type="submit"
              form="board-code-form"   // 폼 외부에서도 제출 가능
              className="btn btn-primary"
            >
              저장
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => navigate('/board-codes', { state: { M_ComCode, M_ComName } })}
            >
              목록으로
            </button>
          </div>

          {/* 작성 폼 박스 */}
          <div className="boardcode-body boardcode-basic">
            <h6>{M_ComName ? `${M_ComName} | ` : ''}📝 새 게시판 코드 등록</h6>
            <BoardCodeForm
          editableCode onSubmit={handleSubmit} formId="board-code-form" />
          </div>

          {/* 권한 관리 박스 */}
          <div className="boardcode-body boardcode-basic mt-4">
            <h6>🔐 게시판 권한 설정</h6>

            {/* 권한 추가 버튼들 */}
            <div className="mb-3">
              <label className="form-label">권한 추가:</label>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm"
                  onClick={() => handleAddAuth('read')}
                >
                  읽기 권한 추가
                </button>
                <button
                  type="button"
                  className="btn btn-outline-warning btn-sm"
                  onClick={() => handleAddAuth('write')}
                >
                  쓰기 권한 추가
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleAddAuth('manage')}
                >
                  관리 권한 추가
                </button>
              </div>
            </div>

            {/* 권한 목록 */}
            {Array.isArray(boardAuths) && boardAuths.length > 0 && (
              <div className="mb-3">
                <label className="form-label">설정된 권한:</label>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th>대상 유형</th>
                        <th>대상 ID</th>
                        <th>대상 이름</th>
                        <th>권한 유형</th>
                        <th>관리</th>
                      </tr>
                    </thead>
                    <tbody>
                      {boardAuths.map((auth, index) => (
                        <tr key={index}>
                          <td>
                            <span className={`badge ${auth.target_type === 'user' ? 'bg-primary' : 'bg-info'}`}>
                              {getTargetTypeLabel(auth.target_type)}
                            </span>
                          </td>
                          <td>{auth.target_id}</td>
                          <td>{auth.target_name || '-'}</td>
                          <td>
                            <span className={`badge ${
                              auth.auth_type === 'manage' ? 'bg-danger' :
                              auth.auth_type === 'write' ? 'bg-warning' : 'bg-success'
                            }`}>
                              {getAuthTypeLabel(auth.auth_type)}
                            </span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleRemoveAuth(index)}
                            >
                              삭제
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* OrgSelect 팝업 */}
          {showOrgSelect && (
            <OrgSelect
              comCode={M_ComCode}
              onConfirm={handleOrgSelectConfirm}
              onCancel={handleOrgSelectCancel}
              selectionMode="multiple"
              targetType="all"
              initialSelected={[]}
            />
          )}

        </div>
      </div>
    </div>
  );

}

export default BoardCodeCreatePage;